import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Studio } from '@src/types.ts/general';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getClipAspectRatio,
  getClipPlaybackId,
} from './StoryManagerFolderView/clipDisplay';
import { buildTargetUrl, getStoryOpenTab } from './utils';

import type { DashboardStory, OtherVideo } from '@src/types.ts/story';

/**
 * Navigation from Story Manager listings into the Content Studio. Shared by
 * the folder view's story/clip rows and the search results dropdown so both
 * paths build identical target URLs.
 *
 * `resetStoryStates` is optional to mirror the folder view's historical
 * "no-op without recentStoryProps" guard.
 */
export function useStoryNavigation(
  resetStoryStates?: (storyId: string) => void,
) {
  const videoCreator = useVideoCreatorStore();
  const navigate = useNavigate();

  const openStory = useCallback(
    (story: DashboardStory) => {
      if (!resetStoryStates) return;
      resetStoryStates(story.id);
      const search = new URLSearchParams(window.location.search);
      // A story opens on Video Clips if it has clips, else its transcript; a
      // clip (below) keeps the Video Clips grid with its preview.
      const targetUrl = `/${Studio.content}${buildTargetUrl(
        search,
        story.id,
        story?.finalVideo?.videoFilePrimary?.video?.muxPlaybackId,
        getStoryOpenTab(story),
      )}`;
      navigate(targetUrl);
    },
    [resetStoryStates, navigate],
  );

  // Opening a clip from the dashboard: there's no story loaded yet, so we
  // navigate to the parent story's content studio with the clip preview set
  // (mirrors the retired Clips tab in ClipDashboardContent).
  const openClip = useCallback(
    (clip: OtherVideo, parentStory: DashboardStory) => {
      if (!resetStoryStates) return;
      resetStoryStates(parentStory.id);
      videoCreator.videoClipPreview = {
        ...clip,
        selectedDimension: getClipAspectRatio(clip),
      };
      const search = new URLSearchParams(window.location.search);
      const targetUrl = `/${Studio.content}${buildTargetUrl(
        search,
        parentStory.id,
        getClipPlaybackId(clip),
      )}`;
      navigate(targetUrl);
    },
    [resetStoryStates, navigate, videoCreator],
  );

  return { openStory, openClip };
}
